/* eslint-disable max-lines-per-function */
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { isDecisionMaker } from './decisionMaker';
import { fetchGammaPetitionDetailsExperiments } from './experiments';
import type { GammaPetitionDetailsDataExperimentsQuery } from './experiments.graphql';
import { isGdprConsentProvided } from './gdprConsentProvided';
import { normalizePlatypusPetitionSignGooglePrefillExperiment } from './normalize/normalizePlatypusPetitionSignGooglePrefillExperiment';
import { normalizeSupporterGrowthEmbedSignPetitionFormExperiment } from './normalize/normalizeSupporterGrowthEmbedSignPetitionFormExperiment';
import type { PetitionViewerInfo } from './petitionViewerInfo';
import { getPetitionViewerInfo } from './petitionViewerInfo';
import { getRecentUserPetition } from './recentUserPetition';

type Experiments = GammaPetitionDetailsDataExperimentsQuery['experiments'];
type SupporterGrowthEmbedSignPetitionFormExperiment = Experiments['supporterGrowthEmbedSignPetitionFormExperiment'];
export type SupporterGrowthEmbedSignPetitionFormExperimentVariation =
	SupporterGrowthEmbedSignPetitionFormExperiment['variation'];

export type PetitionDetailsPageUserData = PetitionViewerInfo & {
	userId: string | undefined;
	decisionMaker: boolean;
	gdprConsentProvided: boolean;
	signatureThermometerEnabled: boolean;
	isSunriseEnabled: boolean;
	shareVisualsExperimentEnabled: boolean;
	isMobileAppDownloadBannerEnabled: boolean;
	isMobileAppDownloadAndroidBannerEnabled: boolean;
	fullSunrisePetitionPageShareComponentExperiment: boolean;
	fullSunrisePetitionHeaderExperiment: 'control' | 'fake_control' | 'variant_a' | 'variant_b';
	fullSunriseActionPanelExperimentVariation: string;
	platypusPetitionSignGooglePrefillExperiment: 'control' | 'fake_control' | 'variant1';
	petitionPageMomentumBarExperiment: 'control' | 'enabled';
	supporterGrowthEmbedSignPetitionFormExperimentVariation: SupporterGrowthEmbedSignPetitionFormExperimentVariation;
	starterDashboardPromptingBannerEnabled: boolean;
	experiments: Omit<
		Experiments,
		| 'sunriseSupportersV1'
		| 'supporterShareVisualsExperiment'
		| 'signatureThermometerExperimentDesktop'
		| 'fullSunrisePetitionPageShareComponentExperiment'
		| 'fullSunrisePetitionHeaderExperiment'
		| 'actionPanelSunriseExperiment'
		| 'mobileAppDownloadBanner'
		| 'mobileAppDownloadAndroidBanner'
		| 'platypusPetitionSignGooglePrefillExperiment'
		| 'petitionPageMomentumBarExperiment'
		| 'supporterGrowthEmbedSignPetitionFormExperiment'
	>;
};

async function normalizeExperiments(utilityContext: UtilityContext, experiments: Experiments) {
	return {
		supporterGrowthEmbedSignPetitionFormExperimentVariation:
			await normalizeSupporterGrowthEmbedSignPetitionFormExperiment({
				experiment: experiments.supporterGrowthEmbedSignPetitionFormExperiment,
				utilityContext,
			}),
	};
}

export async function getPetitionDetailsPageUserData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		currentUserId,
		isGuest,
		decisionMakerHash,
	}: {
		currentUserId: string | undefined;
		isGuest: boolean;
		decisionMakerHash?: string;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageUserData> {
	const [{ petitionId, ...petitionViewerInfo }, decisionMaker, gdprConsentProvided, recentPetition, experiments] =
		await Promise.all([
			getPetitionViewerInfo(utilityContext, slugOrId, currentUserId),
			!isGuest || decisionMakerHash
				? isDecisionMaker(utilityContext, { slugOrId, decisionMakerHash })
				: Promise.resolve(false),
			isGdprConsentProvided(utilityContext),
			isGuest ? Promise.resolve(undefined) : getRecentUserPetition(utilityContext),
			// FIXME remove 'Gamma' from function and operation name
			fetchGammaPetitionDetailsExperiments(utilityContext),
		]);

	const {
		sunriseSupportersV1,
		supporterShareVisualsExperiment,
		signatureThermometerExperimentDesktop,
		fullSunrisePetitionPageShareComponentExperiment,
		fullSunrisePetitionHeaderExperiment,
		actionPanelSunriseExperiment,
		mobileAppDownloadBanner,
		mobileAppDownloadAndroidBanner,
		platypusPetitionSignGooglePrefillExperiment: platypusPetitionSignGooglePrefillExperimentRaw,
		petitionPageMomentumBarExperiment,
		...experimentsRest
	} = experiments;

	const platypusPetitionSignGooglePrefillExperiment = await normalizePlatypusPetitionSignGooglePrefillExperiment({
		experiment: platypusPetitionSignGooglePrefillExperimentRaw,
		utilityContext,
	});

	const normalizedEmbeddedSignFormExperiment = await normalizeExperiments(utilityContext, experiments);

	return {
		...petitionViewerInfo,
		...normalizedEmbeddedSignFormExperiment,
		userId: currentUserId,
		decisionMaker,
		gdprConsentProvided,
		signatureThermometerEnabled: signatureThermometerExperimentDesktop.variation === 'v1',
		isSunriseEnabled: sunriseSupportersV1.variation === 'enabled',
		shareVisualsExperimentEnabled: supporterShareVisualsExperiment.variation === 'v1',
		isMobileAppDownloadBannerEnabled: mobileAppDownloadBanner.variation === 'enabled',
		isMobileAppDownloadAndroidBannerEnabled: mobileAppDownloadAndroidBanner.variation === 'enabled',
		fullSunrisePetitionPageShareComponentExperiment:
			fullSunrisePetitionPageShareComponentExperiment.variation === 'enabled',
		fullSunrisePetitionHeaderExperiment: fullSunrisePetitionHeaderExperiment.variation,
		fullSunriseActionPanelExperimentVariation: actionPanelSunriseExperiment.variation,
		platypusPetitionSignGooglePrefillExperiment,
		petitionPageMomentumBarExperiment: petitionPageMomentumBarExperiment.variation,
		starterDashboardPromptingBannerEnabled: isStarterDashboardPromptingBannerEnabled({
			petitionId,
			ownPetition: petitionViewerInfo.ownPetition,
			recentPetition,
		}),
		experiments: experimentsRest,
	};
}

function isStarterDashboardPromptingBannerEnabled({
	petitionId,
	ownPetition,
	recentPetition,
}: {
	petitionId?: string;
	ownPetition: boolean;
	recentPetition: { id: string } | undefined;
}) {
	if (!recentPetition || !petitionId) return false;
	if (!ownPetition) return false;
	if (recentPetition.id !== petitionId) return false;
	return true;
}
